import React from "react";

import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import RestaurantMenuRoundedIcon from "@mui/icons-material/RestaurantMenuRounded";
import { Box, Theme, Typography } from "@mui/material";
import { makeStyles } from "tss-react/mui";

import { colors } from "@/theme/overrides/colors";

const HIGHLIGHTS = [
  {
    Icon: RestaurantMenuRoundedIcon,
    title: "Personalized meal plans in minutes",
    description: "Build and reuse plans tailored to every client's goals.",
  },
  {
    Icon: InsightsRoundedIcon,
    title: "Adherence & readiness at a glance",
    description: "See who is on track and who needs a nudge — instantly.",
  },
  {
    Icon: CalendarMonthRoundedIcon,
    title: "Book sessions with built-in scheduling",
    description:
      "Manage appointments and availability without a third-party tool.",
  },
];

const useStyles = makeStyles()((theme: Theme) => ({
  panel: {
    position: "relative",
    overflow: "hidden",
    display: "none",
    flexDirection: "column",
    justifyContent: "space-between",
    flexBasis: "46%",
    minWidth: 0,
    padding: "56px 56px 48px",
    color: colors.white,
    background: `linear-gradient(155deg, #2C7A6B 0%, ${colors.theme.primary.darker} 46%, ${colors.theme.primary.default} 100%)`,
    WebkitFontSmoothing: "antialiased",
    [theme.breakpoints.up("md")]: {
      display: "flex",
    },
  },
  // Soft decorative glows for depth on the flat gradient.
  glowTop: {
    position: "absolute",
    top: "-140px",
    right: "-120px",
    width: "360px",
    height: "360px",
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0) 70%)",
    pointerEvents: "none",
  },
  glowBottom: {
    position: "absolute",
    bottom: "-160px",
    left: "-100px",
    width: "320px",
    height: "320px",
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0) 70%)",
    pointerEvents: "none",
  },
  content: {
    position: "relative",
    zIndex: 1,
    display: "flex",
    flexDirection: "column",
  },
  wordmark: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    animation: "$fadeUp 600ms cubic-bezier(0.2, 0, 0, 1) both",
  },
  wordmarkDot: {
    width: "12px",
    height: "12px",
    borderRadius: "50%",
    backgroundColor: colors.theme.tertiary.default,
    boxShadow: "0 0 0 4px rgba(255, 149, 36, 0.28)",
  },
  wordmarkText: {
    fontWeight: 800,
    letterSpacing: "-0.5px",
    color: colors.white,
  },
  headline: {
    marginTop: "56px",
    fontWeight: 800,
    lineHeight: 1.15,
    letterSpacing: "-1px",
    textWrap: "balance",
    maxWidth: "460px",
    animation: "$fadeUp 600ms cubic-bezier(0.2, 0, 0, 1) both",
    animationDelay: "80ms",
  },
  subhead: {
    marginTop: "18px",
    maxWidth: "420px",
    color: "rgba(255, 255, 255, 0.86)",
    textWrap: "pretty",
    animation: "$fadeUp 600ms cubic-bezier(0.2, 0, 0, 1) both",
    animationDelay: "160ms",
  },
  highlights: {
    marginTop: "44px",
    display: "flex",
    flexDirection: "column",
    gap: "22px",
  },
  highlight: {
    display: "flex",
    alignItems: "flex-start",
    gap: "16px",
    animation: "$fadeUp 600ms cubic-bezier(0.2, 0, 0, 1) both",
  },
  highlightIcon: {
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "44px",
    height: "44px",
    borderRadius: "12px",
    backgroundColor: "rgba(255, 255, 255, 0.16)",
    border: "1px solid rgba(255, 255, 255, 0.22)",
    "& svg": { fontSize: "22px" },
  },
  highlightTitle: {
    fontWeight: 700,
    lineHeight: 1.3,
  },
  highlightDescription: {
    color: "rgba(255, 255, 255, 0.78)",
    lineHeight: 1.4,
  },
  footer: {
    position: "relative",
    zIndex: 1,
    marginTop: "48px",
    paddingTop: "24px",
    borderTop: "1px solid rgba(255, 255, 255, 0.18)",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    color: "rgba(255, 255, 255, 0.82)",
    animation: "$fadeUp 600ms cubic-bezier(0.2, 0, 0, 1) both",
    animationDelay: "320ms",
  },
  "@keyframes fadeUp": {
    from: { opacity: 0, transform: "translateY(12px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
}));

const BrandPanel: React.FC = () => {
  const { classes } = useStyles();

  return (
    <Box className={classes.panel}>
      <div className={classes.glowTop} />
      <div className={classes.glowBottom} />

      <div className={classes.content}>
        <div className={classes.wordmark}>
          <span className={classes.wordmarkDot} />
          <Typography variant="h6" className={classes.wordmarkText}>
            NutriCoach
          </Typography>
        </div>

        <Typography variant="h3" className={classes.headline}>
          Everything you need to coach nutrition, in one place.
        </Typography>
        <Typography variant="body1" className={classes.subhead}>
          Meal plans, appointments, and progress tracking — built for
          professional nutrition coaches.
        </Typography>

        <div className={classes.highlights}>
          {HIGHLIGHTS.map(({ Icon, title, description }, index) => (
            <div
              key={title}
              className={classes.highlight}
              style={{ animationDelay: `${240 + index * 80}ms` }}
            >
              <div className={classes.highlightIcon}>
                <Icon />
              </div>
              <div>
                <Typography variant="body1" className={classes.highlightTitle}>
                  {title}
                </Typography>
                <Typography
                  variant="body2"
                  className={classes.highlightDescription}
                >
                  {description}
                </Typography>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className={classes.footer}>
        <LockRoundedIcon sx={{ fontSize: "18px" }} />
        <Typography variant="body2">
          Protected with two-factor authentication
        </Typography>
      </div>
    </Box>
  );
};

export default BrandPanel;
